import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiUser, FiInfo, FiBarChart2 } = FiIcons;

const UserInfo = ({ userProfile }) => {
  const { t } = useLanguage();

  if (!userProfile) return null;

  const calculateIMC = () => {
    const heightM = userProfile.height_cm / 100;
    return (userProfile.weight_kg / (heightM * heightM)).toFixed(1);
  };

  const getIMCCategory = (imc) => {
    if (imc < 18.5) return { label: t('underweight'), color: 'text-blue-500', bg: 'bg-blue-50' };
    if (imc < 25) return { label: t('normal'), color: 'text-emerald-500', bg: 'bg-emerald-50' };
    if (imc < 30) return { label: t('overweight'), color: 'text-amber-500', bg: 'bg-amber-50' };
    return { label: t('obese'), color: 'text-rose-500', bg: 'bg-rose-50' };
  };

  const imc = calculateIMC();
  const imcCategory = getIMCCategory(parseFloat(imc));
  
  const getFitnessLevelLabel = (level) => {
    switch(level) {
      case 'sedentar': return t('sedentary');
      case 'incepator': return t('beginner');
      case 'intermediar': return t('intermediate');
      case 'avansat': return t('advanced');
      default: return level;
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 h-full">
      <div className="flex items-center gap-2 mb-6">
        <SafeIcon icon={FiUser} className="text-blue-500 text-xl" />
        <h3 className="text-lg font-bold text-slate-800">
          {userProfile.firstName ? userProfile.firstName : t('active_profile')}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-3 bg-slate-50 rounded-2xl">
          <div className="text-xs text-slate-500 mb-1">{t('age_sex')}</div>
          <div className="font-semibold text-slate-800 capitalize">
            {userProfile.age} {t('years')}, {userProfile.sex === 'femeie' ? t('female') : t('male')}
          </div>
        </div>
        <div className="p-3 bg-slate-50 rounded-2xl">
          <div className="text-xs text-slate-500 mb-1">{t('body')}</div>
          <div className="font-semibold text-slate-800">
            {userProfile.height_cm}cm / {userProfile.weight_kg}kg
          </div>
        </div>
      </div>

      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">{t('bmi_calc')}</span>
          <span className={`text-xs font-bold px-2 py-1 rounded-lg ${imcCategory.bg} ${imcCategory.color}`}>
            {imcCategory.label}
          </span>
        </div>
        <div className="text-3xl font-bold text-slate-800 tracking-tight">{imc}</div>
        <div className="mt-3 text-xs text-slate-500 leading-relaxed flex gap-2">
          <SafeIcon icon={FiInfo} className="flex-shrink-0 mt-0.5" />
          {t('optimized_for')} {getFitnessLevelLabel(userProfile.fitnessLevel)}
        </div>
      </div>
    </div>
  );
};

export default UserInfo;